import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getDocument } from 'pdfjs-dist';
import { classifyFromUrl } from '../mime.utils';
import { DocumentMimeKind } from '../types';
import { buildDocumentTextCache, PageTextCache } from '../locate/find-engine';
import './worker';

export function usePdfDocument(
    url: string,
    onResetRef: React.MutableRefObject<() => void>,
    onCancelRenderRef: React.MutableRefObject<() => void>
) {
    const pdfDocRef = useRef<any>(null);
    const loadSeqRef = useRef(0);
    const pageCacheRef = useRef<PageTextCache[]>([]);
    const [mimeKind, setMimeKind] = useState<DocumentMimeKind>('unsupported');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [pageCount, setPageCount] = useState(1);
    const [pageIndex, setPageIndex] = useState(0);
    const [hasText, setHasText] = useState(true);
    const [textReady, setTextReady] = useState(false);

    const resetLoadState = useCallback(() => {
        setPageIndex(0);
        pageCacheRef.current = [];
        setHasText(true);
        setTextReady(false);
    }, []);

    useEffect(() => {
        let cancelled = false;
        const seq = ++loadSeqRef.current;
        setLoading(true);
        setError(null);
        onResetRef.current();
        resetLoadState();
        if (pdfDocRef.current) {
            try { pdfDocRef.current.destroy(); } catch { }
            pdfDocRef.current = null;
        }

        const run = async () => {
            if (!url) {
                if (!cancelled && seq === loadSeqRef.current) {
                    setMimeKind('unsupported');
                    setError(null);
                    setLoading(false);
                }

                return;
            }
            const classified = await classifyFromUrl(url);
            if (cancelled || seq !== loadSeqRef.current) {
                return;
            }
            setMimeKind(classified.mimeKind);

            if (classified.mimeKind === 'image') {
                setHasText(false);
                setTextReady(false);
                setPageCount(1);
                setPageIndex(0);
                setLoading(false);
                return;
            }

            if (classified.mimeKind !== 'pdf') {
                setError('Unsupported file type');
                setLoading(false);
                return;
            }

            try {
                const loadingTask = getDocument({
                    url,
                    withCredentials: false,
                    enableScripting: false,
                    isEvalSupported: false,
                    enableXfa: false
                });
                const pdf = await loadingTask.promise;
                if (cancelled || seq !== loadSeqRef.current) {
                    pdf.destroy();
                    return;
                }
                pdfDocRef.current = pdf;
                setPageCount(pdf.numPages);
                setPageIndex(0);
                const cache = await buildDocumentTextCache(pdf);
                if (cancelled || seq !== loadSeqRef.current) {
                    return;
                }
                pageCacheRef.current = cache.pages;
                setHasText(cache.hasText);
                setTextReady(cache.hasText);
                setPageIndex(0);
                setLoading(false);
            } catch {
                if (!cancelled && seq === loadSeqRef.current) {
                    setError('UNABLE TO DISPLAY FILE');
                    setLoading(false);
                }
            }
        };

        run();
        return () => {
            cancelled = true;
            onCancelRenderRef.current();
        };
    }, [url, onResetRef, onCancelRenderRef, resetLoadState]);

    return {
        mimeKind,
        loading,
        error,
        pageCount,
        pageIndex,
        setPageIndex,
        pdfDocRef,
        pageCacheRef,
        hasText,
        setHasText,
        textReady,
        setTextReady,
        setPageCount
    };
}
